// 261010 Epic 2 / P4b-2 — hộp "Thêm hướng" / "Sửa hướng" của tab Bối cảnh. Luật kiểm + nhắc nằm ở locationAssetsPolicy.ts (thuần, có test); file này chỉ là vỏ antd.
// Mã hướng chỉ nhập lúc THÊM (BE không cho sửa: storyboard bám `view_key`). FE kiểm trước ⇒ sai thì KHÔNG gọi API.
import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal } from 'antd';
import type { LocationView } from '../../types/filmLocationAssets';
import type { ActionError } from './workflowActionPolicy';
import { editNotice, editPatch, validateViewForm, type ViewFormErrors, type ViewFormValues } from './locationAssetsPolicy';

export type FormMode = { kind: 'add'; initial: ViewFormValues; existingKeys: string[]; /** dòng chú "từ shot nào" (khi mở từ "Thêm hướng này") */ hint?: string }
  | { kind: 'edit'; view: LocationView };

interface Props {
  mode: FormMode | null;
  /** Gọi API; trả lỗi chữ (đã qua `workflowActionError`) hoặc null nếu xong. */
  onSubmitAdd: (v: ViewFormValues) => Promise<ActionError | null>;
  onSubmitEdit: (view: LocationView, patch: { label?: string; directionText?: string }) => Promise<ActionError | null>;
  onClose: () => void;
}

const LocationViewFormModal: React.FC<Props> = ({ mode, onSubmitAdd, onSubmitEdit, onClose }) => {
  const [values, setValues] = useState<ViewFormValues>({ label: '', directionKey: '', directionText: '' });
  const [errors, setErrors] = useState<ViewFormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // mở hộp mới ⇒ nạp lại giá trị đầu
  useEffect(() => {
    if (!mode) return;
    setErrors({}); setApiError(null); setSending(false);
    setValues(mode.kind === 'add' ? mode.initial : { label: mode.view.label, directionKey: mode.view.directionKey, directionText: mode.view.directionText });
  }, [mode]);

  if (!mode) return null;
  const isAdd = mode.kind === 'add';
  const notice = mode.kind === 'edit' ? editNotice(mode.view, values.directionText) : null;
  const set = (k: keyof ViewFormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setValues((v) => ({ ...v, [k]: e.target.value })); setErrors((x) => ({ ...x, [k]: undefined })); };

  const submit = async () => {
    setApiError(null);
    if (mode.kind === 'add') {
      const errs = validateViewForm(values, mode.existingKeys);
      setErrors(errs);
      if (Object.keys(errs).length) return; // sai ⇒ không gọi API
      setSending(true);
      const err = await onSubmitAdd({ label: values.label.trim(), directionKey: values.directionKey.trim(), directionText: values.directionText.trim() });
      setSending(false);
      if (err) setApiError(err.text); else onClose();
      return;
    }
    const r = editPatch(mode.view, values);
    if (r.errors) { setErrors(r.errors); return; }
    if (!r.patch) { onClose(); return; } // không đổi gì ⇒ không gọi API
    setSending(true);
    const err = await onSubmitEdit(mode.view, r.patch);
    setSending(false);
    if (err) setApiError(err.text); else onClose();
  };

  return (
    <Modal open title={isAdd ? 'Thêm hướng' : `Sửa hướng · ${mode.view.label}`} okText={isAdd ? 'Thêm hướng' : 'Lưu'} cancelText="Huỷ" onOk={submit} onCancel={() => { if (!sending) onClose(); }}
      confirmLoading={sending} maskClosable={false} destroyOnClose>
      <div className="wf" style={{ background: 'transparent', padding: 0, minHeight: 0 }}>
        {isAdd && mode.hint ? <p className="wf-note" style={{ marginTop: 0 }}>{mode.hint}</p> : null}
        <div className="wf-form">
          <label htmlFor="wf-vf-label">Nhãn hướng</label>
          <Input id="wf-vf-label" value={values.label} onChange={set('label')} maxLength={255} status={errors.label ? 'error' : undefined} placeholder="Ví dụ: Nhìn chéo từ bên trái đường" />
          {errors.label ? <div className="wf-form-err" role="alert">{errors.label}</div> : null}
          <label htmlFor="wf-vf-key">Mã hướng</label>
          {isAdd
            ? <Input id="wf-vf-key" value={values.directionKey} onChange={set('directionKey')} maxLength={64} status={errors.directionKey ? 'error' : undefined} placeholder="ben-trai (chữ thường, số, gạch nối)" className="mono" />
            : <code className="wf-form-code" id="wf-vf-key">{mode.view.directionKey}</code>}
          {!isAdd ? <div className="faint" style={{ fontSize: 12 }}>Mã hướng không sửa được (storyboard bám theo mã).</div> : null}
          {errors.directionKey ? <div className="wf-form-err" role="alert">{errors.directionKey}</div> : null}
          <label htmlFor="wf-vf-text">Mô tả hướng</label>
          <Input.TextArea id="wf-vf-text" value={values.directionText} onChange={set('directionText')} rows={4} status={errors.directionText ? 'error' : undefined} />
          {errors.directionText ? <div className="wf-form-err" role="alert">{errors.directionText}</div> : null}
        </div>
        {notice ? <Alert type="warning" showIcon style={{ marginTop: 10 }} message={notice} data-testid="wf-edit-notice" /> : null}
        {apiError ? <Alert type="error" showIcon style={{ marginTop: 10 }} message={apiError} data-testid="wf-form-api-error" /> : null}
      </div>
    </Modal>
  );
};

export default LocationViewFormModal;
